import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { motion } from 'framer-motion';
import { Home, User, Calendar, MessageSquare, Wallet } from 'lucide-react';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, theme } = useApp();
  const [unreadCount, setUnreadCount] = useState(0);

  // Don't show bottom nav on login, register, or welcome pages
  const hideBottomNav = ['/', '/login', '/register', '/role-selection', '/lawyer-profile', '/profile'].includes(location.pathname);

  if (hideBottomNav) {
    return null;
  }

  const translations = {
    english: {
      home: 'Home',
      lawyers: 'Find Lawyer',
      bookings: 'Bookings',
      chat: 'Chats',
      wallet: 'Wallet'
    },
    hindi: {
      home: 'होम',
      lawyers: 'वकील खोजें',
      bookings: 'बुकिंग',
      chat: 'चैट',
      wallet: 'वॉलेट'
    }
  };

  const t = translations[language] || translations.english;

  // Load unread messages count from localStorage
  useEffect(() => {
    const loadUnreadCount = () => {
      try {
        const storedChats = JSON.parse(localStorage.getItem('userChats') || '[]');
        const totalUnread = storedChats.reduce((total, chat) => total + (chat.unreadCount || 0), 0);
        setUnreadCount(totalUnread);
      } catch (error) {
        console.error('Error loading unread count:', error);
        setUnreadCount(0);
      }
    };

    loadUnreadCount();

    // Refresh unread count when window gains focus
    const handleFocus = () => {
      loadUnreadCount();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const navItems = [
    {
      id: 'home',
      label: t.home,
      icon: <Home className="w-5 h-5" />,
      path: '/dashboard',
      isActive: location.pathname === '/dashboard'
    },
    {
      id: 'lawyers',
      label: t.lawyers,
      icon: <User className="w-5 h-5" />,
      path: '/find-lawyer',
      isActive: location.pathname === '/find-lawyer' || location.pathname.startsWith('/lawyer-detail')
    },
    {
      id: 'bookings',
      label: t.bookings,
      icon: <Calendar className="w-5 h-5" />,
      path: '/my-bookings',
      isActive: location.pathname === '/my-bookings'
    },
    {
      id: 'chat',
      label: t.chat,
      icon: <MessageSquare className="w-5 h-5" />,
      path: '/chats',
      isActive: location.pathname === '/chats' || location.pathname.startsWith('/chat-conversation')
    },
    {
      id: 'wallet',
      label: t.wallet,
      icon: <Wallet className="w-5 h-5" />,
      path: '/wallet',
      isActive: location.pathname === '/wallet'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="bottom-nav">
      <div className="bottom-nav-content">
        <div className="bottom-nav-items">
          {navItems.map((item) => (
            <motion.button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`bottom-nav-item relative ${
                item.isActive 
                  ? 'active' 
                  : ''
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="relative">
                {React.cloneElement(item.icon, { 
                  className: 'w-5 h-5'
                })}
                {/* Red dot notification for unread messages */}
                {item.id === 'chat' && unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-[8px] font-bold">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  </div>
                )}
              </div>
              <span className="text-xs font-medium leading-tight">
                {item.label}
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BottomNavigation;